import { cloneElement } from 'react';

import { headingTagFor } from '../headingLevels.js';
import { useFeedbackUiTranslate } from '../hooks/useFeedbackUiTranslate.js';
import { ExternalSettingsLink } from './ExternalSettingsLink.jsx';

/**
 * The ONE feedback tone vocabulary, at its runtime owner (the `TONE_CLASS` map below is typed by it, so a tone
 * added to one and not the other is refused by the checker). JSDOC-PROP-CONTRACT-001 TONE.
 *
 * @typedef {'success'|'warning'|'error'|'info'|'tip'} InlineFeedbackTone
 */

/**
 * The tones an OUTCOME or a STATUS line carries (a list action's outcome, `<ActionBar status>`, a save's result):
 * every feedback tone but the static `tip`.
 *
 * @typedef {Exclude<InlineFeedbackTone, 'tip'>} FeedbackTone
 */

/**
 * Canonical `.alert-<tone>` class per feedback tone (composes shared-styles;
 * introduces no new CSS).
 *
 * @type {Record<InlineFeedbackTone, string>}
 */
const TONE_CLASS = {
  success: 'alert-success',
  warning: 'alert-warning',
  error: 'alert-error',
  info: 'alert-info',
  tip: 'alert-tip',
};

/** The canonical compact alert variant (`feedback.css`). */
const COMPACT_CLASS = 'alert-sm';

/** The dismiss control's glyph — punctuation, not copy (the NAME is the aria-label). */
const DISMISS_GLYPH = '×';

/**
 * Where the live region sits: the whole line (the default), its title alone — or NOWHERE (`none`): a static
 * note that is part of the document (an article callout, a TL;DR, a preview note, a parse-step notice), which
 * renders `role="note"` and never announces, whatever its tone.
 */
const ANNOUNCE = Object.freeze({ ALL: 'all', TITLE: 'title', NONE: 'none' });

/**
 * One action in the row: the host's own element, a same-tab anchor, the new-tab link, or a button —
 * all in the `.alert-action-button` chrome. A destination (`href`) is never also a handler, and the
 * host's element wins over both.
 *
 * @param {{action: FeedbackActionSpec, testId: string}} props
 * @returns {React.JSX.Element}
 */
function FeedbackAction({ action, testId }) {
  const actionTestId = action.testId ?? `${testId}-action-${action.key}`;
  if (action.element) {
    // The host's OWN element (a router `<Link>`) in the row's chrome: the chrome class joins whatever
    // the host dressed it in, and the row's testid applies unless the element carries one.
    const { className: hostClass, 'data-testid': hostTestId } = action.element.props;
    return cloneElement(action.element, {
      className: ['alert-action-button', hostClass].filter(Boolean).join(' '),
      'data-testid': hostTestId ?? actionTestId,
    });
  }
  if (action.href && action.target === '_self') {
    return (
      <a href={action.href} className="alert-action-button" data-testid={actionTestId}>
        {action.label}
      </a>
    );
  }
  if (action.href) {
    return (
      <ExternalSettingsLink
        href={action.href}
        className="alert-action-button"
        testId={actionTestId}
      >
        {action.label}
      </ExternalSettingsLink>
    );
  }
  return (
    <button
      type="button"
      className="alert-action-button"
      onClick={action.onClick}
      disabled={action.disabled === true}
      data-testid={actionTestId}
      {...(action.tourId ? { 'data-tour-id': action.tourId } : {})}
    >
      {action.label}
    </button>
  );
}

/**
 * The message region — ONE element either way: a growing `<span>` for a plain sentence (B-11), a
 * `<div>` holding title, sentence, block body and details list for anything more.
 *
 * @param {object} props
 * @param {boolean} props.block - anything more than a plain sentence
 * @param {'p'|'h2'|'h3'|'h4'|'h5'|'h6'} props.titleTag
 * @param {string|null} props.titleRole - the live region's role when it sits on the title
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.message]
 * @param {React.ReactNode} [props.body]
 * @param {Array<{id: string, text: React.ReactNode, testId?: string}>|null} props.detailRows
 * @param {string} props.testId
 * @returns {React.JSX.Element}
 */
function MessageRegion({ block, titleTag, titleRole, title, message, body, detailRows, testId }) {
  if (!block) {
    // `.alert-content` (flex: 1) — the message region GROWS, so the actions and the dismiss
    // control that follow sit at the row's end. As a bare <span> it did not, and they were
    // parked mid-row wherever the sentence happened to stop (B-11).
    return <span className="alert-content">{message}</span>;
  }
  const TitleTag = titleTag;
  return (
    <div className="alert-content">
      {title ? (
        <TitleTag className="alert-title" {...(titleRole ? { role: titleRole } : {})}>
          {title}
        </TitleTag>
      ) : null}
      {message ? <p className="alert-message">{message}</p> : null}
      {body}
      {detailRows ? (
        <ul className="alert-list">
          {detailRows.map((detail) => (
            <li
              key={detail.id}
              className="alert-detail-text"
              data-testid={detail.testId ?? `${testId}-detail-${detail.id}`}
            >
              {detail.text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * What every action carries.
 *
 * @typedef {object} FeedbackActionBase
 * @property {string} key
 * @property {string} [testId]
 * @property {string} [tourId]
 * @property {boolean} [disabled]
 */

/**
 * An action in the row is exactly one of three: the host's own element (a router `<Link>`, whose
 * words it reads instead of `label`), a destination (`href`: a new tab, or this one with
 * `target: '_self'`), or a button. A destination is never also a handler, and the host's element
 * wins over both.
 *
 * @typedef {FeedbackActionBase & (
 *   {
 *     element: React.ReactElement<{className?: string, 'data-testid'?: string}>,
 *     label?: React.ReactNode,
 *     href?: string,
 *     target?: '_blank'|'_self',
 *     onClick?: React.MouseEventHandler<HTMLButtonElement>,
 *   } | {
 *     element?: undefined,
 *     label: React.ReactNode,
 *     href: string,
 *     target?: '_blank'|'_self',
 *     onClick?: React.MouseEventHandler<HTMLButtonElement>,
 *   } | {
 *     element?: undefined,
 *     label: React.ReactNode,
 *     href?: undefined,
 *     target?: undefined,
 *     onClick?: React.MouseEventHandler<HTMLButtonElement>,
 *   }
 * )} FeedbackActionSpec
 */

/**
 * @typedef {object} InlineFeedbackBaseProps
 * @property {InlineFeedbackTone} [tone='info']
 * @property {React.ReactNode} [message] - the sentence; with no title and no body, falsy → nothing
 * @property {React.ReactNode} [title] - a title before the sentence (`.alert-title`)
 * @property {2|3|4|5|6} [headingLevel] - render the title as this heading; absent → a paragraph
 * @property {React.ReactNode} [children] - block content under the sentence, rendered as given
 * @property {React.ReactNode} [icon] - a leading icon, rendered first inside the root (dress it
 *   `.alert-icon`)
 * @property {string} [ariaLabel] - the root's accessible name (a note that names itself, a TL;DR)
 * @property {Record<`data-${Lowercase<string>}`, string>} [dataAttributes] - `data-*` attributes
 *   for the root, as given (a page-level anchor such as a CMS row's key); any other key, and an
 *   upper-case one, is refused
 * @property {string} [id] - the root's id, for a field's `aria-describedby`
 * @property {string} [className] - appended to the root's classes (spacing)
 * @property {FeedbackActionSpec[]} [actions] - the things the reader can do about it; an empty
 *   array renders no action row
 * @property {Array<{id: string, text: React.ReactNode, testId?: string}>} [details] - the
 *   sentences behind the message (already translated); an empty array renders none
 * @property {() => void} [onDismiss] - present → renders a `.btn-close`
 * @property {string} [dismissLabel] - close-button aria-label (i18n by caller); absent → this
 *   package's own `close` leaf
 * @property {'default'|'compact'} [density='default'] - `compact` composes the canonical
 *   `.alert-sm` (a lighter line inside a dense surface). PLN-DEPT-PLAN-001 R6-B.
 * @property {boolean} [busy=false] - `aria-busy="true"` on the alert while something the line
 *   reports on is being re-checked (`<SetupNotice checking>`); omitted otherwise.
 * @property {string} [testId='inline-feedback']
 * @property {React.Ref<HTMLDivElement>} [ref] - the root element, for a caller that must MEASURE
 *   the line (the build-update band publishes its own height); React 19's ref-as-a-prop, never a
 *   wrapper
 */

/**
 * `announce` — where the live region sits (see the component). `'title'` puts it on the title,
 * which the line then MUST have: the component throws without one.
 *
 * @typedef {InlineFeedbackBaseProps & (
 *   {announce?: 'all'|'none'} | {announce: 'title', title: React.ReactNode}
 * )} InlineFeedbackProps
 */

/**
 * InlineFeedback — a generic, dismissible in-flow feedback banner.
 *
 * The ecosystem-shared pattern for post-action success / status / error feedback
 * that lives IN the page flow (as opposed to a transient toast — planner-web and
 * page-shell have no toast system). Composes the canonical `.alert` utilities;
 * renders nothing when it has nothing to say — no `message`, no `title`, no block
 * body — so callers can pass `message` unconditionally.
 *
 * A11y: `error` uses `role="alert"` (assertive); every other tone uses
 * `role="status"` (polite). The close button is rendered only when `onDismiss`
 * is supplied; its `dismissLabel` is passed in already-translated by the caller, and defaults
 * to this package's own `feedbackUi` leaf (`close`).
 *
 * Actions (PLN-LB-UX-013): a feedback line may offer the reader what to DO about it — the
 * labor budget's target status offers "Use the effective rate" / "Enter hours instead" /
 * "Revisit the minimums". They render as `.alert-actions` > `.alert-action-button` (both
 * canonical in shared-styles `feedback.css`), after the message and before the dismiss
 * control, so the alert's semantic colour carries into the affordance. Labels arrive
 * already-translated (the host owns an action's words).
 *
 * An action with an `href` is a DESTINATION (PLN-DEPT-GRAIN-004 1a, owner ruling R-DS-7): it
 * renders as the ONE new-tab settings link, `<ExternalSettingsLink>`, dressed in the same
 * `.alert-action-button` chrome as its button siblings — with the new-tab glyph and sr-only
 * sentence that link carries. The setup notice's "Group departments in Settings" is the case:
 * before this the fix's destination could only sit in the sentence as a bare verb. `href` wins
 * over `onClick`: a destination is never also a handler. A destination in THIS tab —
 * `target: '_self'`, the cross-app exchange banner's "Sign in directly", where the reader is
 * unauthenticated and has no draft a new tab would protect — renders as a plain anchor in the same
 * chrome: no `target`, no `rel`, no glyph, no sentence (SAVE-FEEDBACK-CANON-001 P3, finding F7e).
 *
 * An action carrying the host's OWN `element` (SAVE-FEEDBACK-CANON-001 P3, owner ruling R-SF-4 "Yes,
 * add it") is a same-tab IN-APP destination: a router `<Link>`, a client-side navigation this
 * router-free leaf cannot import. The host hands the row the element and the leaf dresses it in
 * the same `.alert-action-button` chrome beside whatever class the host gave it — the element's
 * own words, `href` and tab, no glyph, no sentence; the row's default testid applies unless the
 * element carries one. `element` wins over `href` and `onClick`. The budget discovery prompt's
 * "Open Sales Planning settings" is the case.
 *
 * Details (UAT-BLOCKERS-2026-09-18): a feedback line may carry the sentences BEHIND it — the rules
 * a blocked shift claim broke, each side's issues on a trade. They render as `.alert-list` under the
 * message inside `.alert-content` (both canonical in shared-styles `feedback.css`), so the reasons
 * read as part of the one announcement. palm-web's marketplace notices hand-built a card for exactly
 * this, because the slot did not exist here.
 *
 * ## The block shape (SAVE-FEEDBACK-CANON-001 P3 — findings F7a–d, 2026-10-13)
 *
 * Thirteen hand-written `.alert*` elements in six packages could not become this component because
 * it had no `id` (four sites wrapped it in a `<div id>` for an `aria-describedby`), no spacing slot
 * (nine sites wrapped it for a margin), no title (three carried `.alert-title`) and no room for block
 * content (a number line, a support paragraph, a `<dl>`, a ticking clock). The message region is ONE
 * element either way:
 *
 * - a plain sentence: `<span class="alert-content">{message}</span>` — the growing region of B-11;
 * - anything more: `<div class="alert-content">`, holding IN THIS ORDER an optional title
 *   (`<p class="alert-title">`, or `<h{headingLevel} class="alert-title">` when the line is a page
 *   state whose title belongs in the outline), the sentence (`<p class="alert-message">`), the block
 *   body (`children`, rendered as given — the caller supplies block elements), then the details list.
 *
 * `className` is appended to the root's class string (spacing only — the ActionBar precedent);
 * `id` lands on the root and nowhere else, so `aria-describedby={id}` reads the whole line.
 *
 * ## `announce` — which element is the live region
 *
 * By default the ROOT carries the role, so the whole line is read once when it appears. A line whose
 * body RE-RENDERS while it is on screen — form-security's lock notice, whose clock ticks every second
 * (Q-UMB-21: inside a live region it was re-announced for up to fifteen minutes) — passes
 * `announce="title"`: the TITLE carries the role, the root carries none, and the body is read by
 * nobody until the reader goes looking. It REFUSES to render without a title: a live region with
 * nothing to announce is a defect, not a configuration.
 *
 * Every prop is optional but one pairing: with no `message`, no `title` and no block body it
 * renders nothing, and every other prop is read only when given; `announce="title"` requires a
 * `title`.
 *
 * @param {InlineFeedbackProps} props
 * @returns {React.JSX.Element|null}
 */
export function InlineFeedback({
  tone = 'info',
  message,
  title,
  headingLevel,
  children,
  announce = ANNOUNCE.ALL,
  icon,
  ariaLabel,
  dataAttributes,
  id,
  className,
  details,
  actions,
  onDismiss,
  dismissLabel,
  density = 'default',
  busy = false,
  testId = 'inline-feedback',
  ref,
}) {
  // Above the early return: a hook cannot sit behind a conditional exit.
  const t = useFeedbackUiTranslate();
  const hasTitle = Boolean(title);
  // `false` and `null` are what a conditional child evaluates to; neither is a body.
  const hasBody = children !== undefined && children !== null && children !== false;
  if (!message && !hasTitle && !hasBody) return null;
  if (announce === ANNOUNCE.TITLE && !hasTitle) {
    throw new Error(
      'InlineFeedback: announce="title" needs a title — a live region with nothing to announce.',
    );
  }
  const toneClass = TONE_CLASS[tone] || TONE_CLASS.info;
  const role = tone === 'error' ? 'alert' : 'status';
  const rootRole = announce === ANNOUNCE.NONE ? 'note' : announce === ANNOUNCE.TITLE ? null : role;
  const titleRole = announce === ANNOUNCE.TITLE ? role : null;
  const actionRow = Array.isArray(actions) && actions.length > 0 ? actions : null;
  const detailRows = Array.isArray(details) && details.length > 0 ? details : null;

  // ── Slots pre-resolved above the return ──────────────────────────────────────
  // `countConditionals()` walks only `ReturnStatement.argument`; the root's optional attributes,
  // the action row and the dismiss control are decided here, the message region in its own
  // component, so the render itself reads as the three things it is.
  /**
   * @type {React.HTMLAttributes<HTMLDivElement> &
   *   {ref?: React.Ref<HTMLDivElement>, 'data-testid': string}}
   */
  const rootProps = {
    className: ['alert', toneClass, density === 'compact' ? COMPACT_CLASS : null, className || null]
      .filter(Boolean)
      .join(' '),
    ...(rootRole ? { role: rootRole } : {}),
    ...(id ? { id } : {}),
    ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
    ...(dataAttributes || {}),
    'data-testid': testId,
    ...(busy ? { 'aria-busy': 'true' } : {}),
    // React 19 hands a function component its `ref` as a prop: the caller that must MEASURE the line
    // (auth-shell's build-update band publishes its height) reaches the root element, never a wrapper.
    ...(ref ? { ref } : {}),
  };

  const region = (
    <MessageRegion
      block={hasTitle || hasBody || detailRows !== null}
      titleTag={headingLevel ? headingTagFor(headingLevel) : 'p'}
      titleRole={titleRole}
      title={title}
      message={message}
      body={hasBody ? children : null}
      detailRows={detailRows}
      testId={testId}
    />
  );

  const actionRowElement = actionRow ? (
    <div className="alert-actions" data-testid={`${testId}-actions`}>
      {actionRow.map((action) => (
        <FeedbackAction key={action.key} action={action} testId={testId} />
      ))}
    </div>
  ) : null;

  const dismiss = onDismiss ? (
    <button
      type="button"
      className="btn-close"
      onClick={onDismiss}
      aria-label={dismissLabel ?? t('close')}
      data-testid={`${testId}-dismiss`}
    >
      {/* `.btn-close` draws NO glyph of its own — every other renderer supplies the ×. This
          one rendered the button EMPTY: an invisible, ~8px dismiss control on every
          dismissible feedback line (and the committed visual baseline recorded exactly
          that — a dismissible error row with no × in it). Decoration only: the accessible
          name stays the aria-label. */}
      <span aria-hidden="true">{DISMISS_GLYPH}</span>
    </button>
  ) : null;

  return (
    <div {...rootProps}>
      {icon ?? null}
      {region}
      {actionRowElement}
      {dismiss}
    </div>
  );
}

export default InlineFeedback;
